export type EnrichmentStatus = 'CACHE_HIT' | 'ENRICHED' | 'MISS' | 'ENRICHING';

export const ENRICHMENT_STATUS = {
  CACHE_HIT: 'CACHE_HIT',
  ENRICHED: 'ENRICHED',
  MISS: 'MISS',
  ENRICHING: 'ENRICHING',
} as const;

export interface EnrichmentUpdateEntry {
  displayName?: string;
  entityId?: string;
  status: EnrichmentStatus;
}

export type EnrichmentUpdate = Record<string, EnrichmentUpdateEntry>;

export interface CallHistoryItem {
  id: string;
  phoneNumber: string;
  name?: string;
  [key: string]: unknown;
}

export interface EnrichmentCacheEntry {
  displayName: string;
  entityId: string;
  provider: string;
  expiresAt: number;
  insertionTs: number;
  lastAccessTs: number;
}

export interface CacheIndexEntry {
  insertionTs: number;
  lastAccessTs: number;
}

export type CacheIndex = Record<string, CacheIndexEntry>;

export interface PhoneLookupLocalCacheOptions {
  userId: string;
  ttlMs?: number;
  maxEntries?: number;
}

export interface BatchLookupResult {
  resultArray: any[];
  foundArray: string[];
  notFoundArray: string[];
}

export interface BatchProgress {
  batchIndex: number;
  totalBatches: number;
  phonesInBatch: string[];
  result: BatchLookupResult;
}

export interface DssClientOptions {
  // Actual Webex SDK instance: expects `webex.internal.dss`
  dss: {
    lookupByPhoneNumbers?: (phoneNumbers: string[]) => Promise<BatchLookupResult>;
  };
  batchDelayMs?: number;
  /** Cap SDK batch lookup wait; avoids 30s Mercury timeout before fallback (default 8000). */
  lookupTimeoutMs?: number;
  maxConcurrentBatches?: number;
  maxRateLimitRetries?: number;
  rateLimitRetryDelayMs?: number;
}

export interface EntitySelectionResult {
  displayName: string;
  entityId: string;
  provider: string;
}

export type DirectorySearchLookup = (
  phone: string
) => Promise<EntitySelectionResult | undefined>;

export type OutlookLookup = (phone: string) => Promise<EntitySelectionResult | undefined>;

export interface PhoneEnricherOptions {
  unknownLabel: string;
  telemetry?: Telemetry;
  /** When true (default), try Directory Search API after DSS batch miss/timeout. */
  enableDirectorySearchFallback?: boolean;
  directorySearchLookup?: DirectorySearchLookup;
  /** When true (default), try Outlook Graph contacts after directory search miss. */
  enableOutlookLookup?: boolean;
  outlookLookup?: OutlookLookup;
}

export interface Telemetry {
  emitAttempt(phone: string, cacheTier: 'hot' | 'warm' | 'miss'): void;
  emitSuccess(phone: string, latencyMs: number, source: 'cache' | 'api'): void;
  emitMiss(phone: string, errorCategory: string): void;
  emitBatch(batchSize: number, latencyMs: number): void;
}

export interface TelemetryBus {
  track: (eventName: string, properties: Record<string, unknown>) => void;
}

export interface TelemetryEvent {
  eventName: string;
  properties: Record<string, string | number | boolean>;
  timestamp: number;
}

export interface PhoneEnrichmentOptions {
  enableEnrichment: boolean;
  userId: string;
  dss?: DssClientOptions['dss'];
  ttlMs?: number;
  maxEntries?: number;
  batchDelayMs?: number;
  /** Max wait per DSS batch before treating as miss (default 8000 ms). */
  lookupTimeoutMs?: number;
  unknownLabel?: string;
  telemetry?: Telemetry;
  contacts?: any; // ContactsIntegration from contacts module (optional)
  /** When true, delay enrichment until contacts load or timeout (call history). */
  waitForContacts?: boolean;
  /** Bumped when contacts integration/data becomes available to re-run enrichment. */
  contactsRefreshSignal?: number;
  /** Try Directory Search API when DSS lookupByPhoneNumbers misses (org mobile numbers). */
  enableDirectorySearchFallback?: boolean;
  /** Try Outlook Graph contacts when directory search misses (personal contacts). */
  enableOutlookLookup?: boolean;
}
